import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AccessTime as TimeIcon,
  Assignment as AssignmentIcon,
  Map as MapIcon,
  Store as StoreIcon,
  Rule as RuleIcon,
  SwapHoriz as TransitionIcon,
  type SvgIconComponent,
} from '@mui/icons-material';

import { NavigateLinks } from '@/components';
import { useUserInfo } from '@/hooks';
import { logisticOperatorsPageBreadcrumbs } from './config';
import { useGetLogisticOperatorByIdQuery } from './services';
import {
  BUSINESS_ROLE_GROUPS,
  userHasPermission,
} from '@/components/security/AppRoles';
import type { ROLES } from '@/components/security/AppRoles';

type DashboardTile = {
  title: string;
  description: string;
  icon: SvgIconComponent;
  path: string;
  /** Si se omite, la tarjeta es visible para todos los roles. */
  permission?: ROLES | readonly ROLES[];
  /** Clases del chip del icono: [texto, fondo]. */
  accent: string;
};

type DashboardGroup = {
  heading: string;
  tiles: DashboardTile[];
};

const ACCENT = {
  orange: 'text-[#EC6B01] bg-[#FFF1E3]',
  blue: 'text-[#1976D2] bg-[#E3F2FD]',
} as const;

const LogisticOperatorDashboard = () => {
  const navigate = useNavigate();
  const { permissions } = useUserInfo();
  const { logisticOperatorId } = useParams<{ logisticOperatorId: string }>();

  if (!logisticOperatorId) {
    throw new Error('El ID del operador logístico es requerido');
  }

  const breadcrumbs = useMemo(
    () => logisticOperatorsPageBreadcrumbs(navigate),
    [navigate]
  );

  const { data: operatorData } = useGetLogisticOperatorByIdQuery(
    { logisticOperatorId },
    { skip: !logisticOperatorId }
  );

  const base = `/business/logistic-operator/${logisticOperatorId}`;

  const groups: DashboardGroup[] = useMemo(
    () => [
      {
        heading: 'Servicios',
        tiles: [
          {
            title: 'Servicios del operador',
            description: 'Crea y administra los servicios que ofrece el operador.',
            icon: AssignmentIcon,
            path: `${base}/services/assignments/create`,
            permission: BUSINESS_ROLE_GROUPS.operations,
            accent: ACCENT.orange,
          },
          {
            title: 'Reglas de ruteo',
            description: 'Estrategia y reglas de ruteo de cada servicio.',
            icon: RuleIcon,
            path: `${base}/service-rules-config`,
            permission: BUSINESS_ROLE_GROUPS.management,
            accent: ACCENT.orange,
          },
          {
            title: 'Franjas horarias',
            description: 'Gestiona los horarios de entrega disponibles.',
            icon: TimeIcon,
            path: `${base}/time-slots`,
            permission: BUSINESS_ROLE_GROUPS.operations,
            accent: ACCENT.orange,
          },
        ],
      },
      {
        heading: 'Red logística',
        tiles: [
          {
            title: 'Zonas',
            description: 'Dibuja y edita las zonas de cobertura en el mapa.',
            icon: MapIcon,
            path: `${base}/zones`,
            permission: BUSINESS_ROLE_GROUPS.adminOrDispatcher,
            accent: ACCENT.blue,
          },
          {
            title: 'Comercios',
            description: 'Comercios asociados a este operador.',
            icon: StoreIcon,
            path: `${base}/commerces`,
            accent: ACCENT.blue,
          },
          {
            title: 'Transiciones',
            description: 'Estados y flujos por los que pasa un envío.',
            icon: TransitionIcon,
            path: `${base}/transitions`,
            accent: ACCENT.blue,
          },
        ],
      },
    ],
    [base]
  );

  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          tiles: group.tiles.filter(
            (tile) =>
              !tile.permission ||
              userHasPermission(permissions, tile.permission)
          ),
        }))
        .filter((group) => group.tiles.length > 0),
    [groups, permissions]
  );

  const renderTile = (tile: DashboardTile) => {
    const Icon = tile.icon;
    return (
      <li key={tile.path}>
        <button
          type='button'
          onClick={() => navigate(tile.path)}
          className='group flex h-full w-full cursor-pointer items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF7500]/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7500]/50'
        >
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${tile.accent}`}
          >
            <Icon fontSize='medium' />
          </span>
          <span className='flex flex-col gap-1'>
            <span className='font-semibold text-gray-800 group-hover:text-[#EC6B01]'>
              {tile.title}
            </span>
            <span className='text-sm leading-snug text-gray-500'>
              {tile.description}
            </span>
          </span>
        </button>
      </li>
    );
  };

  return (
    <section className='flex h-full w-full flex-col'>
      <NavigateLinks
        actualPage={operatorData?.fullName || 'Operador Logístico'}
        breadcrumbs={breadcrumbs}
      />

      <div className='mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center gap-8 overflow-y-auto py-6'>
        {visibleGroups.map((group) => (
          <div key={group.heading} className='flex flex-col gap-3'>
            <h2 className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
              {group.heading}
            </h2>
            <ul className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
              {group.tiles.map(renderTile)}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LogisticOperatorDashboard;
